import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

// Function to generate consistent colors for type_thing values (matches FilterChips)
const getTypeThingColorClass = (typeThing) => {
  if (!typeThing) return 'bg-gray-500';
  
  const colors = [
    'bg-purple-500', 'bg-orange-500', 'bg-red-500', 'bg-pink-500', 
    'bg-indigo-500', 'bg-yellow-500', 'bg-teal-500', 'bg-rose-500',
    'bg-violet-500', 'bg-amber-500', 'bg-cyan-500', 'bg-lime-500'
  ];
  
  let hash = 0;
  for (let i = 0; i < typeThing.length; i++) {
    const char = typeThing.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

// Function to get light version of color for badges
const getTypeThingLightColorClass = (typeThing) => {
  if (!typeThing) return 'bg-gray-100 text-gray-800';
  
  const lightColors = [
    'bg-purple-100 text-purple-800', 'bg-orange-100 text-orange-800', 'bg-red-100 text-red-800', 'bg-pink-100 text-pink-800',
    'bg-indigo-100 text-indigo-800', 'bg-yellow-100 text-yellow-800', 'bg-teal-100 text-teal-800', 'bg-rose-100 text-rose-800',
    'bg-violet-100 text-violet-800', 'bg-amber-100 text-amber-800', 'bg-cyan-100 text-cyan-800', 'bg-lime-100 text-lime-800'
  ];
  
  let hash = 0;
  for (let i = 0; i < typeThing.length; i++) {
    const char = typeThing.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  const index = Math.abs(hash) % lightColors.length;
  return lightColors[index];
};

const AdminThingCard = ({ product, onEdit = () => {}, searchQuery = '', highlightSearchTerm = null }) => {
  const handleEdit = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    onEdit(product);
  };

  if (!product) {
    return (
      <div className="animate-pulse bg-muted rounded-lg p-4 h-full flex flex-col space-y-3">
        <div className="bg-muted-foreground/20 rounded aspect-[3/4] w-full" />
        <div className="h-4 bg-muted-foreground/20 rounded w-3/4" />
        <div className="h-3 bg-muted-foreground/10 rounded w-1/2" />
        <div className="h-3 bg-muted-foreground/10 rounded w-2/3" />
      </div>
    );
  }

  return (
    <div onClick={handleEdit} className="group block cursor-pointer">
      <div className="bg-card rounded-lg shadow-subtle hover:shadow-modal transition-gentle hover-lift overflow-hidden h-full flex flex-col relative">
        <div className="absolute top-2 right-2 z-20">
          <div className={`${getTypeThingColorClass(product.type_thing)} text-white text-xs font-bold px-2 py-1 rounded-full`}>
            {product.type_thing || 'Sin tipo'}
          </div>
        </div>

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-200 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
            <div className="flex items-center gap-2 text-slate-700">
              <Icon name="Edit" size={16} />
              <span className="text-sm font-medium">Editar {product.type_thing || 'producto'}</span>
            </div>
          </div>
        </div>

        <div className="relative aspect-[3/4] overflow-hidden bg-muted flex-shrink-0 flex items-center justify-center"
             style={{
               backgroundImage: 'url(/assets/images/background_image.png)',
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               backgroundRepeat: 'no-repeat'
             }}>
          <div className="relative w-[60px] h-[80px] sm:w-[120px] sm:h-[170px] flex items-center justify-center">
            <Image
              src={product.image_urls?.[0] || '/placeholder.jpg'}
              alt={`${product.title_thing}`}
              className="w-full h-full object-cover rounded"
              low
            />
          </div>
        </div>

        <div className="p-2 sm:p-3 flex-1 flex flex-col">
          <h3 className="font-heading font-bold text-base sm:text-lg text-card-foreground line-clamp-2 mb-1 group-hover:text-primary transition-smooth flex-1">
            {highlightSearchTerm && searchQuery ? 
              highlightSearchTerm(product.title_thing && product.title_thing.length > 25 ? `${product.title_thing.substring(0, 25)}...` : product.title_thing, searchQuery) :
              (product.title_thing && product.title_thing.length > 25 ? `${product.title_thing.substring(0, 25)}...` : product.title_thing)
            }
          </h3>
          <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
            {highlightSearchTerm && searchQuery ? 
              highlightSearchTerm(product.description_thing && product.description_thing.length > 50 
                ? `${product.description_thing.substring(0, 50)}...` 
                : product.description_thing || 'Sin descripción', searchQuery) :
              (product.description_thing && product.description_thing.length > 50 
                ? `${product.description_thing.substring(0, 50)}...` 
                : product.description_thing || 'Sin descripción')
            }
          </p>
          <div className="flex items-center justify-between mb-2 flex-shrink-0">
            <div className="flex items-center space-x-1">
              <span className="font-semibold text-xs sm:text-sm text-primary">
                ${product.price_thing?.toFixed(2) || 'N/A'}
              </span>
            </div>
          </div>

          <div className="mb-2 flex-shrink-0 space-y-1">
            {/* Mobile: Stack vertically */}
            <div className="flex flex-col space-y-2 sm:hidden">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <span className={`${getTypeThingLightColorClass(product.type_thing)} px-1.5 py-0.5 rounded-full`}>
                  {product.type_thing || 'Sin tipo'}
                </span>
              </div>
              
              <div className={`px-2 py-1 rounded-full text-xs font-medium self-start ${
                product.available_thing === true
                  ? 'bg-success/10 text-success border border-success/20'
                  : 'bg-destructive/10 text-destructive border border-destructive/20'
              }`}>
                {product.available_thing === true ? 'Disponible' : 'No disponible'}
              </div>
            </div>

            {/* Desktop: Side by side */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex-1"></div>
              
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                product.available_thing === true
                  ? 'bg-success/10 text-success border border-success/20'
                  : 'bg-destructive/10 text-destructive border border-destructive/20'
              }`}>
                {product.available_thing === true ? 'Disponible' : 'No disponible'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminThingCard;

